import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { useSelector } from "react-redux";
import { routes } from "../../../constants/routes";
import {
  getPaymentBillingDetails,
  downloadInvoice,
} from "../../../api/provider-section/provider-service-management";
import { useSetLoading } from "../../../common/hooks/appearance";
import "./view-payment-details.scss";
import { AlertComponent, ServiceList } from "../../../common/components";
import { useNavigate, useLocation } from "react-router-dom";
import ShareEmailPopup from "./invoice-share";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";
import BookingStatus from "./booking-status";

const ViewPaymentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isLoggedIn = useSelector((state: any) => state.session.isLoggedIn);

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string>("-");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = location.state?.orderId;
  const customerName = location.state?.customerName;
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedInvoiceOption, setSelectedInvoiceOption] = useState<
    "view" | "download" | "share" | null
  >(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const breadcrumbLink = [
    {
      label: "Payments & Billing",
      stateData: { ...location.state },
      link: routes.PAYMENTS_AND_BILLING,
    },
    {
      label: `${orderId}-${customerName}`,
      link: "#",
    },
  ];

  const fetchRequestDetails = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await getPaymentBillingDetails(id, timezone);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data || {});
      } else {
        setError("Failed to fetch request details.");
      }
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message ||
        "An error occurred while fetching data.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const handleBackArrowClick = () => {
    navigate(routes.PAYMENTS_AND_BILLING, {
      state: {
        searchText: location.state?.searchText || "",
        activeFilters: location.state?.activeFilters || {},
      },
    });
  };

  const handleViewDocument = async (id: string) => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await downloadInvoice(id, timezone);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const fileUrl = window.URL.createObjectURL(blob);
      window.open(fileUrl, "_blank");
      setTimeout(() => {
        window.URL.revokeObjectURL(fileUrl);
      }, 1000);
      setInvoiceOpen(false);
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message ||
        "Unable to open document. Please try again.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setInvoiceOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await downloadInvoice(id, timezone);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const fileNameId = orderId?.toString().slice(-4);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${fileNameId}.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setInvoiceOpen(false);
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message ||
        "An error occurred while downloading the invoice.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setInvoiceOpen(false);
    } finally {
      setLoading(false);
    }
  };
  const formattedServices = useMemo(() => {
    if (!data?.services) return [];
    return (data?.services || []).map((service: any) => ({
      serviceId: service?.serviceId,
      serviceName: service?.serviceName,
      needType: service?.needType,

      subServices: (service?.items?.subServices || []).map((sub: any) => ({
        name: sub?.name,
        amount: Number(sub?.price || 0),
      })),

      merchandise: (service?.items?.merchandise || []).map((m: any) => ({
        name: m?.name,
        amount: Number(m?.total || 0),
        quantity: Number(m?.quantity || 1),
        price: Number(m?.price || 0),
      })),
    }));
  }, [data]);
  const isPreNeedOrder = data?.payment?.orderProductType === "pre-need" || data?.payment?.orderProductType === "preneed" || data?.payment?.orderProductType === "pre_need" || data?.payment?.orderProductType === "Preneed";
  const isPaymentSuccessful = [
    "paid",
    "succeeded",
    "confirmed",
    "completed",
    "refunded",
  ].includes(data?.payment?.status?.toLowerCase() || "");
  const isInvoiceDisabled = !(
    data?.order?.allowInvoiceDownload ||
    isPaymentSuccessful ||
    (isPreNeedOrder &&
      ["paid", "confirmed"].includes(data?.order?.status?.toLowerCase() || ""))
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setInvoiceOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="view-provider-request-details-main-wrapper">
      <ProfileHeader
        breadcrumbLink={breadcrumbLink}
        showBackArrow={true}
        handleBackArrowClick={handleBackArrowClick}
        mode="provider"
      />
      <div className="details-page-inner">
        {data && (
          <div>
            <div className="provider-card">
              <div className="payment-details-header">
                <h3>Details</h3>
                <Tooltip
                  title={
                    isInvoiceDisabled
                      ? "Invoice is available only after successful payment."
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClick}
                      disabled={isInvoiceDisabled}
                      endIcon={
                        <span
                          style={{
                            fontSize: "12px",
                            display: "inline-block",
                            transition: "transform 0.2s ease",
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        >
                          ▾
                        </span>
                      }
                      sx={{
                        background: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: "13px",
                        color: "#374151",
                        "&:hover": {
                          background: "#f3f4f6",
                        },
                        "&.Mui-disabled": {
                          background: "#f9fafb",
                          color: "#9ca3af",
                          borderColor: "#e5e7eb",
                        },
                      }}
                    >
                      Invoice
                    </Button>
                  </span>
                </Tooltip>

                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleViewDocument(id);
                      handleClose();
                    }}
                  >
                    View
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleDownloadInvoice();
                      handleClose();
                    }}
                  >
                    Download
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setShareOpen(true);
                      handleClose();
                    }}
                  >
                    Share
                  </MenuItem>
                </Menu>
              </div>

              <div className="separator"></div>
              <div className="payment-details-body">
                <div className="details-col">
                  <div className="detail-row">
                    <span className="label">Customer</span>
                    <span>-</span>
                    <div className="customer-info">
                      <span className="value">
                        {`${data?.user?.first_name || ""} ${data?.user?.last_name || ""}` ||
                          "-"}
                      </span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="label">Payment Status</span>
                    <span>-</span>
                    <span className="value">
                      {isPreNeedOrder ? (
                        "-"
                      ) : (
                        <BookingStatus
                          status={
                            data?.payment?.status
                              ? data.payment.status.charAt(0).toUpperCase() +
                              data.payment.status.slice(1)
                              : "Not Initiated"
                          }
                        />
                      )}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Created On</span>
                    <span>-</span>
                    <span className="value">
                      {data?.order?.created_at || "N/A"}
                    </span>
                  </div>
                  {data?.payment?.displayPaidUnpaid && (
                    <div className="detail-row">
                      <span className="label">Paid(Billed)</span>
                      <span>-</span>
                      <span className="value bold">
                        {data?.payment?.paidAmount != null
                          ? `$${data.payment.paidAmount}`
                          : "-"}
                      </span>
                    </div>
                  )}

                  <div className="detail-row">
                    <span className="label">My Earning</span>
                    <span>-</span>
                    <span className="value">${data?.partnerEarning || 0}</span>
                  </div>
                </div>
                <div className="details-col">
                  <div className="detail-row">
                    <span className="label">Order ID</span>
                    <span>-</span>
                    <span className="value">
                      {data?.orderId || orderId || "-"}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="label">AFP Commission</span>
                    <span>-</span>
                    <span className="value">${data?.afpCommission || 0}</span>
                  </div>

                  <div className="detail-row">
                    <span className="label">Total Amount</span>
                    <span>-</span>
                    <span className="value bold">
                      ${data?.totalAmount || 0}
                    </span>
                  </div>
                  {data?.payment?.displayPaidUnpaid && (
                    <div className="detail-row">
                      <span className="label">Unpaid(Unbilled)</span>
                      <span>-</span>
                      <span className="value bold">
                        {data?.payment?.unpaidAmount != null
                          ? `$${data.payment.unpaidAmount}`
                          : "-"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="payment-description-wrapper">
              <div className="payment-description-card">
                <ServiceList
                  services={formattedServices}
                  tax={data?.tax || 0}
                  total={data?.totalAmount || 0}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
      <ShareEmailPopup
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        id={id}
        orderId={orderId}
        customerName={customerName}
      />
    </div>
  );
};

export default ViewPaymentDetails;
